import { createContext, use, type ComponentType, type ReactNode } from 'react'

/** The person a chooser hands back: a User Profile's public id and name. */
export interface ChosenPerson {
  readonly id: string
  readonly name: string
}

/** What a person field gives the chooser it opens. */
export interface PersonChooserProps {
  readonly open: boolean
  /** The dialog's title, such as "Choose Account Manager". */
  readonly title: string
  /** The person the field holds, or `null`. */
  readonly selected: string | null
  readonly onClose: () => void
  /** A person was chosen; the field takes them and the chooser closes. */
  readonly onChoose: (person: ChosenPerson) => void
}

/** A chooser of User Profiles, such as Site Management's user chooser. */
export type PersonChooser = ComponentType<PersonChooserProps>

const PersonChooserContext = createContext<PersonChooser | null>(null)

/**
 * The person field's chooser slot (GW-29): Site Management fills it with its user chooser from
 * its feature's app-wide provider. Until one is given, a person field shows its value and can be
 * cleared, but no person can be chosen. Keep `chooser` the same component between renders.
 */
export function PersonChooserProvider(props: { readonly chooser: PersonChooser; readonly children: ReactNode }) {
  return <PersonChooserContext value={props.chooser}>{props.children}</PersonChooserContext>
}

/** The chooser a person field opens, or `null` before one is given. */
export function usePersonChooser(): PersonChooser | null {
  return use(PersonChooserContext)
}
